import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";
import { FernausleseChannelMappingsSection } from "@/pages/stammdaten/schnittstellen/FernausleseChannelMappingsSection";

interface FernausleseSettings {
  enabled: boolean;
  base_url: string | null;
  api_username: string | null;
  api_path: string;
  api_password_configured: boolean;
}

interface TenantSettings {
  fernauslese?: FernausleseSettings;
}

interface FernausleseIntegrationPanelProps {
  canWrite: boolean;
}

export function FernausleseIntegrationPanel({ canWrite }: FernausleseIntegrationPanelProps) {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [apiPath, setApiPath] = useState("VERWALTUNGSSOFTWARE");
  const [passwordConfigured, setPasswordConfigured] = useState(false);
  const [testChannel, setTestChannel] = useState("1");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        setEnabled(settings.fernauslese?.enabled ?? false);
        setBaseUrl(settings.fernauslese?.base_url ?? "");
        setUsername(settings.fernauslese?.api_username ?? "");
        setApiPath(settings.fernauslese?.api_path ?? "VERWALTUNGSSOFTWARE");
        setPasswordConfigured(settings.fernauslese?.api_password_configured ?? false);
        setPassword("");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (location.hash !== "#fernauslese-kanaele") return;
    const element = document.getElementById("fernauslese-kanaele");
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, loading]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    setTestResult(null);
    try {
      const body: {
        enabled: boolean;
        base_url: string | null;
        api_username: string | null;
        api_path: string;
        api_password?: string;
      } = {
        enabled,
        base_url: baseUrl.trim() || null,
        api_username: username.trim() || null,
        api_path: apiPath.trim() || "VERWALTUNGSSOFTWARE",
      };
      if (password.trim()) {
        body.api_password = password;
      } else if (!passwordConfigured) {
        body.api_password = "";
      }
      await api.patch<TenantSettings>("/api/v1/settings/fernauslese", body);
      setSuccess(true);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fernauslese konnte nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    if (!canWrite) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const channel = Number(testChannel) || 1;
      const result = await api.post<{
        value_kwh: string;
        state: number;
        usage_kwh: string;
        error: number;
        target_kwh: string;
        label: string | null;
      }>("/api/v1/settings/fernauslese/test", { channel });
      setTestResult(
        `Kanal ${channel}${result.label ? ` (${result.label})` : ""}: ${result.value_kwh} kWh gesamt, Verbrauch seit Einschalten ${result.usage_kwh} kWh, Zustand ${result.state}, Fehler ${result.error}${Number(result.target_kwh) > 0 ? `, Ziel ${result.target_kwh} kWh` : ""}`,
      );
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : "Verbindungstest fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <div className="space-y-6">
      <Card title="API-Verbindung" bodyClassName="space-y-5">
        <form onSubmit={(e) => void onSave(e)} className="space-y-5">
          <FormHint>
            Steuerung der Stromsteckdosen über die{" "}
            <a
              href="https://fernauslese.de/anleitung/api-zfa-io/"
              target="_blank"
              rel="noreferrer"
              className="text-accent underline"
            >
              Fernauslese.de API
            </a>
            . Live Zählerstände unter{" "}
            <Link to="/strom" className="text-accent underline">
              Strom
            </Link>{" "}
            einsehen.
          </FormHint>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={!canWrite}
            />
            Fernauslese-Integration aktivieren
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-label-secondary">Basis-URL (z. B. https://bcm.zfa.io)</span>
              <input
                className="eh-input"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://kuerzel.zfa.io"
                disabled={!canWrite}
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-label-secondary">API-Benutzername</span>
              <input
                className="eh-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="api_bcm"
                disabled={!canWrite}
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-label-secondary">
                API-Pfad (z. B. mainkur oder VERWALTUNGSSOFTWARE; /v1 wird automatisch ergänzt)
              </span>
              <input
                className="eh-input"
                value={apiPath}
                onChange={(e) => setApiPath(e.target.value)}
                placeholder="mainkur"
                disabled={!canWrite}
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-label-secondary">
                API-Passwort
                {passwordConfigured ? " (gespeichert)" : ""}
              </span>
              <input
                type="password"
                className="eh-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={passwordConfigured ? "Leer lassen = unverändert" : ""}
                disabled={!canWrite}
                autoComplete="new-password"
              />
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm">
              <span className="mb-1 block text-label-secondary">Test-Kanal</span>
              <input
                type="number"
                min={1}
                className="eh-input w-24"
                value={testChannel}
                onChange={(e) => setTestChannel(e.target.value)}
                disabled={!canWrite}
              />
            </label>
            {canWrite ? (
              <Button type="button" variant="secondary" loading={testing} onClick={() => void onTest()}>
                Verbindung testen
              </Button>
            ) : null}
          </div>
          {testResult ? <p className="text-sm text-label-secondary">{testResult}</p> : null}
          {error ? <p className="text-sm text-status-red">{error}</p> : null}
          {success ? (
            <p className="text-sm text-status-green">Fernauslese-Einstellungen gespeichert.</p>
          ) : null}
          {canWrite ? (
            <div className="flex justify-end border-t border-separator pt-4">
              <Button type="submit" loading={saving}>
                Speichern
              </Button>
            </div>
          ) : null}
        </form>
      </Card>

      <FernausleseChannelMappingsSection canWrite={canWrite} />
    </div>
  );
}
